import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Header, Switch } from '@robocorp/components';
import { LocalStorageItemKeys, useLocalStorage } from '~/hooks';
import { RawEditor } from './components/RawEditor';
import { SimpleEditor } from './components/SimpleEditor';
import type { JSONEditorProps } from './types';

type Props = JSONEditorProps & {
  title?: string;
};

enum EditorMode {
  Simple = 'simple',
  Raw = 'raw',
}

/**
 * Visual JSON type data editor
 */
export const JSONEditor: FC<Props> = ({ title, ...props }) => {
  const { t } = useTranslation();

  const { storageValue: mode, setStorageValue: setMode } = useLocalStorage<EditorMode>({
    key: LocalStorageItemKeys.JSON_EDITOR_MODE,
    initialValue: EditorMode.Simple,
  });

  const onToggleMode = useCallback(() => {
    setMode(mode === EditorMode.Raw ? EditorMode.Simple : EditorMode.Raw);
  }, [mode, setMode]);

  return (
    <Box>
      <Header size="small">
        {title ? <Header.Title title={title} /> : null}
        <Header.Actions>
          <Switch
            checked={mode === EditorMode.Raw}
            onChange={onToggleMode}
            value={t('jsonEditor.editAsJSON')}
            aria-label={t('jsonEditor.editAsJSON') as string}
            disabled={props.error}
          />
        </Header.Actions>
      </Header>
      {mode === EditorMode.Simple ? <SimpleEditor {...props} /> : <RawEditor {...props} />}
    </Box>
  );
};
